import React, { useState } from 'react';
import { Zap, Edit3 } from 'lucide-react';
import { AutoGenerationForm } from './AutoGenerationForm';
import { ManualCreationForm } from './ManualCreationForm';

interface Step3Props {
  data: any;
  onUpdate: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const Step3GenerationLots: React.FC<Step3Props> = ({
  data,
  onUpdate,
  onNext,
  onBack,
}) => {
  const [generationMode, setGenerationMode] = useState<'auto' | 'manual' | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  if (!generationMode) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Création des lots
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Comment souhaitez-vous créer les lots de votre promotion ?
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button
            onClick={() => setGenerationMode('auto')}
            className="group relative overflow-hidden rounded-xl border-2 border-gray-200 dark:border-gray-700 p-6 text-left hover:border-blue-500 hover:shadow-lg transition-all"
          >
            <div className="absolute top-4 right-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Zap className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Génération automatique
            </h3>

            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Redimmo génère tous les lots automatiquement selon vos critères
            </p>

            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Rapide : 100+ lots en 30 secondes</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Numérotation automatique (A0101, A0102...)</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Prix ajustés selon étage et vue</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Modifiable individuellement après</span>
              </li>
            </ul>

            <div className="mt-6 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium inline-block">
              Recommandé pour 20+ lots
            </div>
          </button>

          <button
            onClick={() => setGenerationMode('manual')}
            className="group relative overflow-hidden rounded-xl border-2 border-gray-200 dark:border-gray-700 p-6 text-left hover:border-purple-500 hover:shadow-lg transition-all"
          >
            <div className="absolute top-4 right-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Edit3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Création manuelle
            </h3>

            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Créez chaque lot individuellement avec tous les détails
            </p>

            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li className="flex items-start">
                <span className="text-purple-500 mr-2">✓</span>
                <span>Contrôle total sur chaque lot</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-500 mr-2">✓</span>
                <span>Personnalisation maximale</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-500 mr-2">✓</span>
                <span>Idéal pour configurations spéciales</span>
              </li>
            </ul>

            <div className="mt-6 px-4 py-2 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-sm font-medium inline-block">
              Pour projets sur-mesure
            </div>
          </button>
        </div>

        <div className="flex justify-between pt-6 border-t dark:border-gray-700">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            ← Retour
          </button>
        </div>
      </div>
    );
  }

  if (generationMode === 'auto') {
    return (
      <AutoGenerationForm
        data={data}
        onUpdate={onUpdate}
        onNext={onNext}
        onBack={() => setGenerationMode(null)}
        onGenerating={setIsGenerating}
        isGenerating={isGenerating}
      />
    );
  }

  return (
    <ManualCreationForm
      data={data}
      onUpdate={onUpdate}
      onNext={onNext}
      onBack={() => setGenerationMode(null)}
    />
  );
};
